<div
  class="import-token-container full-width full-height flex column p-16 gap-24"
>
  <!-- Address Input Section -->
  <div class="input-section flex column gap-12">
    <div class="address-input-row flex gap-8 align-items-end">
      <div class="flex-1">
        <kc-input
          [infoLabel]="{ label: 'Token Contract Address' }"
          [placeholder]="'0x...'"
          [type]="'text'"
          [isValid]="state() !== 'error' && state() !== 'already-imported'"
          [ngModel]="contractAddress()"
          (valueChange)="onAddressChange($any($event))"
        />
      </div>
      <kc-button
        [variant]="'secondary'"
        [size]="'m'"
        [text]="'Paste'"
        class="paste-btn"
        (buttonClick)="pasteFromClipboard()"
      />
    </div>

    @if (state() === "error" || state() === "already-imported") {
      <span class="kc-input-error-text">{{ errorMessage() }}</span>
    }

    @if (showAddressError()) {
      <span class="kc-input-error-text">Invalid contract address format</span>
    }

    <kc-button
      [variant]="'primary'"
      [size]="'m'"
      [text]="state() === 'loading' ? 'Looking up...' : 'Look Up Token'"
      [isDisabled]="!isAddressValid() || state() === 'loading'"
      [isFullWidth]="true"
      (buttonClick)="lookupToken()"
    />
  </div>

  <!-- Loading State -->
  @if (state() === "loading") {
    <div class="loading-section flex column gap-16">
      <div class="token-card card-0 flex align-items-center gap-16 p-16">
        <app-skeleton [height]="'48px'" [width]="'48px'" [fullRound]="true" />
        <div class="flex column gap-8 flex-1">
          <app-skeleton [height]="'20px'" [width]="'140px'" />
          <app-skeleton [height]="'16px'" [width]="'100px'" />
        </div>
      </div>
    </div>
  }

  <!-- Already Imported State -->
  @if (state() === "already-imported" && tokenInfo()) {
    <div class="token-found-section flex column gap-16">
      <!-- Token Info Card -->
      <div class="token-card card-0 flex column gap-16 p-16">
        <div class="token-header flex align-items-center gap-16">
          <div
            class="token-logo-placeholder flex align-items-center justify-content-center"
          >
            <span class="token-symbol-initial typo-title-4">{{
              tokenInfo()!.symbol.charAt(0) || "?"
            }}</span>
          </div>
          <div class="flex column gap-4">
            <div class="text-gray-90 typo-title-4">
              {{ tokenInfo()!.symbol }}
            </div>
            <div class="text-gray-60 typo-text-1">{{ tokenInfo()!.name }}</div>
          </div>
        </div>

        <div class="token-details flex column gap-8">
          <div
            class="detail-row flex justify-content-between align-items-center"
          >
            <span class="text-gray-60 typo-text-1">Address</span>
            <span class="text-gray-90 font-mono">{{
              shortAddress(tokenInfo()!.address)
            }}</span>
          </div>
          <div
            class="detail-row flex justify-content-between align-items-center"
          >
            <span class="text-gray-60 typo-text-1">Balance</span>
            <span class="text-gray-90"
              >{{ formatBalance(tokenInfo()!) }} {{ tokenInfo()!.symbol }}</span
            >
          </div>
          <div
            class="detail-row flex justify-content-between align-items-center"
          >
            <span class="text-gray-60 typo-text-1">Decimals</span>
            <span class="text-gray-90">{{ tokenInfo()!.decimals }}</span>
          </div>
        </div>
      </div>

      <kc-button
        [variant]="'secondary'"
        [size]="'m'"
        [text]="'Close'"
        [isFullWidth]="true"
        (buttonClick)="onCancel()"
      />
    </div>
  }

  <!-- Token Found State -->
  @if (state() === "found" && tokenInfo()) {
    <div class="token-found-section flex column gap-16">
      <div class="section-label typo-text-1 text-gray-60">Token Found</div>

      <!-- Token Info Card -->
      <div class="token-card card-0 flex column gap-16 p-16">
        <div class="token-header flex align-items-center gap-16">
          <div
            class="token-logo-placeholder flex align-items-center justify-content-center"
          >
            <span class="token-symbol-initial typo-title-4">{{
              tokenInfo()!.symbol.charAt(0) || "?"
            }}</span>
          </div>
          <div class="flex column gap-4">
            <div class="text-gray-90 typo-title-4">
              {{ tokenInfo()!.symbol }}
            </div>
            <div class="text-gray-60 typo-text-1">{{ tokenInfo()!.name }}</div>
          </div>
        </div>

        <div class="token-details flex column gap-8">
          <div
            class="detail-row flex justify-content-between align-items-center"
          >
            <span class="text-gray-60 typo-text-1">Address</span>
            <span class="text-gray-90 font-mono">{{
              shortAddress(tokenInfo()!.address)
            }}</span>
          </div>
          <div
            class="detail-row flex justify-content-between align-items-center"
          >
            <span class="text-gray-60 typo-text-1">Balance</span>
            <span class="text-gray-90"
              >{{ formatBalance(tokenInfo()!) }} {{ tokenInfo()!.symbol }}</span
            >
          </div>
          <div
            class="detail-row flex justify-content-between align-items-center"
          >
            <span class="text-gray-60 typo-text-1">Decimals</span>
            <span class="text-gray-90">{{ tokenInfo()!.decimals }}</span>
          </div>
        </div>
      </div>

      <!-- Accept / Cancel Buttons -->
      <div class="action-buttons flex gap-12">
        <kc-button
          [variant]="'secondary'"
          [size]="'m'"
          [text]="'Cancel'"
          class="flex-1"
          (buttonClick)="onCancel()"
        />
        <kc-button
          [variant]="'primary'"
          [size]="'m'"
          [text]="isSaving() ? 'Importing...' : 'Import Token'"
          [isDisabled]="isSaving()"
          [isFullWidth]="true"
          class="flex-1"
          (buttonClick)="onAccept()"
        />
      </div>
    </div>
  }

  <!-- Error State -->
  @if (state() === "error") {
    <div class="error-section flex column align-items-center gap-16 p-16">
      <kc-icon
        [iconClass]="'icon-close'"
        [size]="'lg'"
        [color]="'var(--red-20)'"
      />
      <div class="text-center text-gray-60 typo-text-1">
        {{ errorMessage() }}
      </div>
    </div>
  }
</div>
